import React, { useState, useEffect } from "react";
import { Space, message, Pagination } from "antd";
import Layouts from "../../components/Layouts";
import { getDirectusClient } from "../../lib/directus";
import { readItems } from "@directus/sdk";
import { useSelector } from "react-redux";
import validarpermiso from "../../lib/utils";
import SearchSection from "./components/SearchSection";
import InvoiceTable from "./components/InvoiceTable";
import InvoiceModal from "./components/InvoiceModal";
import { Form } from "antd";

export default function ListFactura() {
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState(null);
  const permisos = useSelector((state) => state.permisos);
  const userBodega = useSelector((state) => state.userdata.bodega?.id);

  // Add pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Add new state variables
  const [searchForm] = Form.useForm();
  const [filters, setFilters] = useState({
    consecutivo: "",
    vendedor: "",
    fecha: null,
  });

  // Update fetchFacturas to handle pagination and include search filters
  const fetchFacturas = async (page = 1, pageSize = 10) => {
    if (!validarpermiso("facturas", "read", permisos)) {
      setHasPermissionError(true);
      return;
    }

    setLoading(true);
    try {
      const directus = await getDirectusClient();

      // Build filter object
      const filterObj = {
        _and: [{ bodega: { _eq: userBodega } }],
      };

      if (filters.consecutivo) {
        filterObj._and.push({
          consecutivo: { _contains: filters.consecutivo },
        });
      }

      if (filters.vendedor) {
        filterObj._and.push({
          vendedor_factura: {
            _or: [
              { first_name: { _contains: filters.vendedor } },
              { last_name: { _contains: filters.vendedor } },
            ],
          },
        });
      }

      if (filters.fecha) {
        const startDate = filters.fecha.startOf("day").toISOString();
        const endDate = filters.fecha.endOf("day").toISOString();
        filterObj._and.push({
          date_created: {
            _between: [startDate, endDate],
          },
        });
      }

      // Get total count with filters
      const countResponse = await directus.request(
        readItems("facturas", {
          aggregate: { count: "id" },
          filter: filterObj,
        })
      );

      const totalItems = countResponse[0]?.count?.id;

      // Get filtered and paginated data
      const response = await directus.request(
        readItems("facturas", {
          fields: [
            "*",
            "cliente.*",
            "vendedor_factura.*",
            "vendedor_prestamo.*",
            "productos.*",
            "productos.producto.*",
            "productos.producto.grupo.impuesto",
            "user_created.first_name",
            "user_created.last_name",
            "productos.seriales.*",
            "productos.seriales.estado.estado",
            "bodega.*",
            "caja.*",
          ],
          filter: filterObj,
          sort: ["-date_created"],
          limit: pageSize,
          offset: (page - 1) * pageSize,
        })
      );

      setFacturas(response);
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
        total: totalItems,
      });
      setHasPermissionError(false);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      if (error.response?.status === 403) {
        setHasPermissionError(true);
        message.error("No tiene permisos para ver facturas");
      }
    } finally {
      setLoading(false);
    }
  };

  // Update handleSearch to use search values directly
  const handleSearch = async (values) => {
    try {
      setLoading(true);
      const directus = await getDirectusClient();

      // Build filter object with the values directly
      const filterObj = {
        _and: [{ bodega: { _eq: userBodega } }],
      };

      if (values.consecutivo) {
        filterObj._and.push({
          consecutivo: { _contains: values.consecutivo },
        });
      }

      if (values.vendedor) {
        filterObj._and.push({
          vendedor_factura: {
            _or: [
              { first_name: { _contains: values.vendedor } },
              { last_name: { _contains: values.vendedor } },
            ],
          },
        });
      }

      if (values.fecha) {
        const startDate = values.fecha.startOf("day").toISOString();
        const endDate = values.fecha.endOf("day").toISOString();
        filterObj._and.push({
          date_created: {
            _between: [startDate, endDate],
          },
        });
      }

      // Get total count with filters
      const countResponse = await directus.request(
        readItems("facturas", {
          aggregate: { count: "id" },
          filter: filterObj,
        })
      );

      const totalItems = countResponse[0]?.count?.id;

      // Get filtered data
      const response = await directus.request(
        readItems("facturas", {
          fields: [
            "*",
            "cliente.*",
            "vendedor_factura.*",
            "vendedor_prestamo.*",
            "productos.*",
            "productos.producto.*",
            "productos.producto.grupo.impuesto",
            "user_created.first_name",
            "user_created.last_name",
            "productos.seriales.*",
            "productos.seriales.estado.estado",
            "bodega.*",
            "caja.*",
          ],
          filter: filterObj,
          sort: ["-date_created"],
          limit: pagination.pageSize,
          offset: 0,
        })
      );

      setFilters(values);
      setFacturas(response);
      setPagination({
        ...pagination,
        current: 1,
        total: totalItems,
      });
    } catch (error) {
      console.error("Error searching invoices:", error);
      message.error("Error al buscar facturas");
    } finally {
      setLoading(false);
    }
  };

  // Update useEffect to use pagination
  useEffect(() => {
    fetchFacturas(pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize]);

  const getStatusColor = (estado) => {
    const colors = {
      creada: "blue",
      anulada: "red",
      pagada: "green",
      pendiente: "gold",
    };
    return colors[estado] || "default";
  };

  const showFacturaDetails = (factura) => {
    setSelectedFactura(factura);
    setIsModalVisible(true);
  };

  // Add this before the return statement
  const handleClear = async () => {
    try {
      setLoading(true);
      searchForm.resetFields();

      const directus = await getDirectusClient();

      // Reset to initial filter with only bodega
      const filterObj = {
        _and: [{ bodega: { _eq: userBodega } }],
      };

      // Get total count with reset filters
      const countResponse = await directus.request(
        readItems("facturas", {
          aggregate: { count: "id" },
          filter: filterObj,
        })
      );

      const totalItems = countResponse[0]?.count?.id;

      // Get fresh data
      const response = await directus.request(
        readItems("facturas", {
          fields: [
            "*",
            "cliente.*",
            "vendedor_factura.*",
            "vendedor_prestamo.*",
            "productos.*",
            "productos.producto.*",
            "productos.producto.grupo.impuesto",
            "user_created.first_name",
            "user_created.last_name",
            "productos.seriales.*",
            "productos.seriales.estado.estado",
            "bodega.*",
            "caja.*",
          ],
          filter: filterObj,
          sort: ["-date_created"],
          limit: pagination.pageSize,
          offset: 0,
        })
      );

      // Reset all states
      setFilters({
        consecutivo: "",
        vendedor: "",
        fecha: null,
      });
      setFacturas(response);
      setPagination({
        ...pagination,
        current: 1,
        total: totalItems,
      });
    } catch (error) {
      console.error("Error resetting data:", error);
      message.error("Error al restablecer los datos");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layouts>
      {!hasPermissionError ? (
        <Space direction="vertical" style={{ width: "100%" }}>
          <SearchSection
            form={searchForm}
            onSearch={handleSearch}
            onClear={handleClear}
          />

          <InvoiceTable
            facturas={facturas}
            loading={loading}
            showFacturaDetails={showFacturaDetails}
            fetchFacturas={() =>
              fetchFacturas(pagination.current, pagination.pageSize)
            }
          />

          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger={true}
            showQuickJumper={true}
            showTotal={(total) => `Total: ${total} facturas`}
            pageSizeOptions={["10", "25", "50", "100"]}
            onChange={(page, pageSize) => {
              setPagination({
                ...pagination,
                current: page,
                pageSize: pageSize,
              });
            }}
            style={{ marginTop: 16, textAlign: "right" }}
            disabled={!validarpermiso("facturas", "read", permisos)}
          />

          <InvoiceModal
            selectedFactura={selectedFactura}
            isModalVisible={isModalVisible}
            setIsModalVisible={setIsModalVisible}
            setSelectedFactura={setSelectedFactura}
            fetchFacturas={() => fetchFacturas(pagination.current, pagination.pageSize)}
         validation={true}
         />
        </Space>
      ) : (
        <h3>No tiene permisos para ver facturas</h3>
      )}
    </Layouts>
  );
}
